import React, { useState, useEffect } from "react";
import {
    Plus,
    Search,
    Edit2,
    Trash2,
    FileText,
    Check,
    Ban,
    DollarSign,
    Clock,
    CheckCircle,
    X,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { useLanguage } from "../../context/LanguageContext";
import { baseURL } from "@/config/APIConfig";
import { getBillColumns } from "./VendorBill/BillColumns";
import { DataTable } from "@/components/ui/table";
import EditInvoiceModel from "./Invoice/EditInvoiceModel";
import { defaultMetadata } from "./Invoice/getInvoiceColumns";
import ExportDialog from "@/components/ExportDialog/ExportDialog";
import { generateExportContent } from "./VendorBill/generateExportContent";
import BillPrintView from "./VendorBill/BillPrintView";
import { useLocation } from "react-router-dom";
import { callLoader } from "@/redux/Loader/actions";
import { useDispatch } from "react-redux";
import { showToast } from "@/redux/Toast/actions";
import { getBookingDetails } from '@/redux/Booking/actions';
import {
    buildArApSyncPayload,
    executeArApSync,
    isArApSyncSuccess,
    SYNC_CHARGE_KIND,
} from "@/utils/accountingSyncUtils";
import { getArrivalNoticeList } from '@/redux/ImportBooking/actions';
import ConfirmSyncDialog from "@/components/ui/ConfirmSyncModal";
import {
    ACCOUNTING_DIVISION,
    getInvoiceMetadataDivision,
} from "@/utils/accountingBookingUtils";
import {
    buildAccountingListQuery,
    buildAccountingQueryParams,
    buildVendorBillPayload,
    accountingFetch,
} from "@/utils/accountingApiUtils";
import {
    JOB_COSTING_MODE,
    resolveBookingPayload,
} from "@/pages/JobCosting/jobCostingUtils";


// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';
const API_URL = baseURL.AiBaseURL;

const STATUS_CONFIG = {
    draft: { label: "Draft", color: "bg-slate-500", icon: Edit2 },
    pending: { label: "Pending", color: "bg-amber-500", icon: Clock },
    approved: { label: "Approved", color: "bg-blue-500", icon: Check },
    partially_paid: { label: "Partial", color: "bg-orange-500", icon: Clock },
    paid: { label: "Paid", color: "bg-green-500", icon: CheckCircle },
    void: { label: "Void", color: "bg-red-500", icon: Ban },
};

const VendorBills = () => {
    const { t } = useLanguage();
    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [editingBill, setEditingBill] = useState(null);
    const [metadata, setMetadata] = useState({ ...defaultMetadata });
    const [printData, setPrintData] = useState(null);
    const [showExport, setShowExport] = useState("");
    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
    const companyId = authUser?.companyId;
    const username = authUser?.firstName + ' ' + authUser?.lastName || authUser?.username || "SYSTEM";

    //for the confirmaton popup when sync to ar/ap
    const [showSyncDialog, setShowSyncDialog] = useState(false);
    const [pendingBillData, setPendingBillData] = useState(null);
    const [autoSync, setAutoSync] = useState(false);
    const [accountingDivision, setAccountingDivision] = useState(
        ACCOUNTING_DIVISION.EXPORT,
    );

    const dispatch = useDispatch();
    const location = useLocation();

    const billQueryParams = (divisionOverride) =>
        buildAccountingQueryParams(
            companyId,
            divisionOverride ?? accountingDivision,
        );

    const [formData, setFormData] = useState({
        bill_date: new Date().toISOString().split("T")[0],
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0],
        customer_id: "",
        customer_name: "",
        vendor_invoice_no: "",
        currency_code: "USD",
        exchange_rate: 1,
        terms: "Net 30",
        status: "draft",
        invoice_type: "BILL",
        notes: "",
        lines: [],
    });

    useEffect(() => {
        fetchBills();
    }, [filterStatus, searchTerm, accountingDivision]);

    useEffect(() => {
        const navDivision = location.state?.division;
        if (
            navDivision === ACCOUNTING_DIVISION.IMPORT ||
            navDivision === ACCOUNTING_DIVISION.EXPORT
        ) {
            setAccountingDivision(navDivision);
        }
    }, [location.state?.division]);

    const fetchBills = async () => {
        try {
            dispatch(callLoader(1));
            const listQuery = buildAccountingListQuery({
                companyId,
                division: accountingDivision,
                filters: {
                    ...(filterStatus ? { status: filterStatus } : {}),
                },
            });
            const data = await accountingFetch(
                `${API_URL}/api/accounting/bills${listQuery}`,
            );

            let filtered = data;
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                filtered = data.filter(
                    (bill) =>
                        bill.bill_no?.toLowerCase().includes(term) ||
                        bill.vendor_name?.toLowerCase().includes(term),
                );
            }

            setBills(filtered);
        } catch (error) {
            toast.error("Failed to load bills");
        } finally {
            setLoading(false);
            dispatch(callLoader(0));
        }
    };

    const handleApprove = async (id, divisionOverride) => {
        try {
            await accountingFetch(
                `${API_URL}/api/accounting/bills/${id}/approve${billQueryParams(divisionOverride)}`,
                { method: "POST" },
            );
            toast.success("Bill approved!");
            fetchBills();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleVoid = async (id, divisionOverride) => {
        if (!window.confirm("Void this bill?")) return;
        try {
            await accountingFetch(
                `${API_URL}/api/accounting/bills/${id}/void${billQueryParams(divisionOverride)}`,
                { method: "POST" },
            );
            toast.success("Bill voided!");
            fetchBills();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const resolveSyncDivision = (doc, override) =>
        override || doc?.division || accountingDivision;

    const handleDelete = async (id, divisionOverride) => {
        if (!window.confirm("Are you sure you want to delete this bill?")) return;
        try {
            const bill = await accountingFetch(
                `${API_URL}/api/accounting/bills/${id}${billQueryParams(divisionOverride)}`,
            );
            const division = resolveSyncDivision(bill, divisionOverride);
            const payload = buildArApSyncPayload({
                document: bill,
                chargeKind: SYNC_CHARGE_KIND.AP,
                syncType: "delete",
                isSync: false,
                createdBy: username,
                division,
            });
            const bookingResponse = await executeArApSync(payload, division);

            if (isArApSyncSuccess(bookingResponse)) {
                await accountingFetch(
                    `${API_URL}/api/accounting/bills/${id}${billQueryParams(bill.division)}`,
                    { method: "DELETE" },
                );
                toast.success("Bill deleted!");
                fetchBills();
            } else {
                throw new Error("SyncArAp failed");
            }
        } catch (error) {
            toast.error(error.message);
        }
    };

    const openEditModal = async (bill) => {
        try {
            dispatch(callLoader(1));
            const billDivision = bill.division || accountingDivision;
            const data = await accountingFetch(
                `${API_URL}/api/accounting/bills/${bill.id}${billQueryParams(billDivision)}`,
            );
            if (data.division) {
                setAccountingDivision(data.division);
            }
            setEditingBill(data);

            setFormData({
                ...data,

                bill_date: data.bill_date || "",
                due_date: data.due_date || "",
                customer_id: data.vendor_id || "",
                vendor_name: data.vendor_name || "",
                vendor_invoice_no: data.vendor_invoice_no || "",
                currency_code: data.currency_code || "USD",
                exchange_rate: data.exchange_rate || 1,
                terms: data.terms || "",
                notes: data.notes || "",
                lines: transformLines(data.lines) || [],
                metadata: data.metadata || { ...defaultMetadata },
            });

            setMetadata(data.metadata || {});
            setShowModal(true);
        } catch (error) {
        } finally {
            dispatch(callLoader(0));
        }
    };

    const resetForm = () => {
        setFormData({
            bill_date: new Date().toISOString().split("T")[0],
            due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
            customer_id: "",
            vendor_name: "",
            invoice_type: "BILL",
            vendor_invoice_no: "",
            currency_code: "USD",
            exchange_rate: 1,
            terms: "Net 30",
            status: "draft",
            notes: "",
            lines: [],
        });
        setMetadata({
            ...defaultMetadata,
            type: "BILL",
            division: getInvoiceMetadataDivision(accountingDivision),
        });
        setEditingBill(null);
        setShowModal(true);
        toast.success("Ready to create a new bill");
    };

    const formatCurrency = (amount) =>
        new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
        }).format(amount || 0);
    const formatDate = (dateStr) =>
        dateStr
            ? new Date(dateStr).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
            })
            : "-";

    const totalOutstanding = bills
        .filter((b) => !["paid", "void"].includes(b.status))
        .reduce((sum, b) => sum + (b.balance_due || 0), 0);

    const changeHandler = (key, value) => {
        setFormData((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const onMail = (invoice) => {
        setShowExport("email");
        generatePrintData(invoice);
    };

    const onPrint = (invoice) => {
        setShowExport("print");
        generatePrintData(invoice);
    };

    const generatePrintData = (invoice) => {
        const metadata = {};
        const fullInvoice = {};

        setPrintData({ ...invoice });
    };
    const columns = getBillColumns({
        openEditModal,
        handleApprove,
        handleDelete,
        handleVoid,
        formatDate,
        formatCurrency,
        STATUS_CONFIG,
        onPrint,
        onMail,
    });

    const prefillData_ = location.state?.prefillData;

    const setPrefillData = (prefillData) => {
        setEditingBill(null);

        const prefillDivision =
            prefillData?.division === ACCOUNTING_DIVISION.IMPORT ||
                prefillData?.division === "Import"
                ? ACCOUNTING_DIVISION.IMPORT
                : prefillData?.division === ACCOUNTING_DIVISION.EXPORT ||
                    prefillData?.division === "Export"
                    ? ACCOUNTING_DIVISION.EXPORT
                    : accountingDivision;
        setAccountingDivision(prefillDivision);

        setMetadata({
            ...defaultMetadata,

            file_reference_no:
                prefillData?.fileNumber || prefillData?.FileRefNumber || "",
            customer_po_number: prefillData?.poNumber,
            BookingNumber:
                prefillData?.carrierBookingNumber ||
                prefillData?.CarrierBookingNo ||
                "",
            origin_name: prefillData?.portOfLoading,
            origin_code: prefillData?.portOfLoadingCode,
            destination_name: prefillData?.portOfDischarge,
            destination_code: prefillData?.portOfDischargeCode,
            descriptions: `${prefillData?.vessel || ""} / ${prefillData?.vesselCode || ""}`,

            type: "BILL",
            division: getInvoiceMetadataDivision(prefillDivision),
            office_type: prefillData?.officeType,
            on_board_date: "",
            eta_date: prefillData?.etaAtDestination,
            weight_kgs: prefillData?.weight,
            measurement_cbm: 0,
            show_wire_transfer: false,
            show_zelle_details: false,
            show_credit_card_details: false,
            email: prefillData?.email,
            profit_center: "",
        });

        setFormData({
            ...formData,

            bill_date: new Date().toISOString().split("T")[0],                                          // YYYY-MM-DD
            due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],      // YYYY-MM-DD

            vendor_id: String(prefillData?.vendorId || ""),
            vendor_name: prefillData?.vendor || "",
            vendor_invoice_no: prefillData?.vendor_invoice_no || "",

            booking_id: String(prefillData?.id || prefillData?.BolID || ""),
            origin_port_id:
                prefillData?.portOfLoadingCode ||
                prefillData?.origin_code ||
                "",
            destination_port_id:
                prefillData?.portOfDischargeCode ||
                prefillData?.destination_code ||
                "",

            currency_id: "USD",
            currency_code: "USD",
            invoice_type: "BILL",
            exchange_rate: 1,

            notes: "",
            subtotal: 0,
            tax_amount: 0,
            total_amount: 0,

            terms: prefillData?.terms || "Net 30",
            notes: prefillData?.notes || "",
            internal_notes: prefillData?.internal_notes || "",

            metadata: metadata || {},

            lines: transformLines(prefillData?.charges || [])

        });

        setShowModal(true);
    }

    useEffect(() => {
        if (!prefillData_) return;

        setPrefillData(prefillData_);

    }, [prefillData_]);


    const transformLines = (lines = []) => {
        return lines.map((item, index) => {
            const quantity = Number(item.quantity) || 1;
            const unitPrice = Number(item.unit_price ?? item.amount) || 0;
            const amount = Number(item.amount) || (quantity * unitPrice);
            const taxRate = Number(item.tax_rate) || 0;
            const taxAmount = Number(item.tax_amount) || 0;
            const totalAmount = Number(item.total_amount) || (amount + taxAmount);

            return {
                line_no: item.line_no ?? 0,
                description: item.description || "",

                // chargeCode: item.chargeCode || "",
                charge_code_id: String(
                    item.chargeCode ||
                    item.CHARGE_CODE ||
                    item.charge_code_id ||
                    "",
                ),
                gl_account_id: item.gl_account_id || "",

                // ✅ NEW FIELDS
                roe: item.roe != null ? Number(item.roe) : null,
                Basis: item?.Basis || item?.basis || "Per Shipment",

                quantity: quantity,
                unit_price: unitPrice,
                amount: amount,

                tax_rate: taxRate,
                tax_amount: taxAmount,
                total_amount: totalAmount,

                currency: item.currency || "",
                exchange_rate: item.roe != null ? Number(item.roe) : null,

                source_charge_id: item.chargeId ? String(item.chargeId) : item.source_charge_id ? String(item.source_charge_id) : "",
                booking_id: item.booking_id || "",

                comments: item.comments || null
            };
        });
    };

    // featch booking detail by id and set data to form
    const loadBookingData = async (id) => {
        dispatch(callLoader(1));
        try {
            const isImport =
                accountingDivision === ACCOUNTING_DIVISION.IMPORT;
            const mode = isImport
                ? JOB_COSTING_MODE.IMPORT
                : JOB_COSTING_MODE.EXPORT;
            const raw = isImport
                ? await dispatch(
                    getArrivalNoticeList({
                        BolID: id,
                        CompanyID: companyId,
                    }),
                )
                : await dispatch(getBookingDetails(id, companyId));

            if (raw) {
                const { booking, apCharges } = resolveBookingPayload(raw, mode);
                const merged = {
                    ...(booking || raw),
                    id: booking?.BolID || booking?.id || id,
                    carrierBookingNumber:
                        booking?.carrierBookingNumber ||
                        booking?.CarrierBookingNo ||
                        raw?.CarrierBookingNo ||
                        "",
                    division: accountingDivision,
                    apCharges: apCharges || [],
                };
                setData(merged, merged.apCharges || []);
            }
        } catch (error) {
            console.error("Error loading booking:", error);
            toast.error("Failed to load booking");
        } finally {
            dispatch(callLoader(0));
        }
    };

    const getVendorFromCharges = (apCharges = []) => {
        const found = (apCharges || []).find(
            (item) =>
                (item.vendorId || item.VENDOR_ID) &&
                (item.vendor || item.VENDOR),
        );

        if (found) {
            return {
                vendor: found.vendor || found.VENDOR,
                vendorId: found.vendorId || found.VENDOR_ID,
                vendorRef: found.vendorRef || found.VENDOR_REF || "",
            };
        }

        return {
            vendor: "",
            vendorId: 0,
            vendorRef: "",
        };
    };

    function setData(data, apCharges) {
        const vendorDetails = getVendorFromCharges(apCharges || []);
        const char_ = (apCharges || []).map((c, i) => ({
            ...c,
            line_no: i + 1,
            description: c.description,
            quantity: Number(c?.quantity),
            unit_price: Number(c?.amount),
            amount: Number(c?.quantity || 0) * Number(c?.amount),
            tax_rate: 0,
            tax_amount: 0,
            total_amount: Number(c?.quantity || 0) * Number(c?.amount),
            roe: c?.roe,
            basis: c?.basis,
            currency: c?.currency,
            source_charge_id: String(c?.id ?? c?.ID ?? ""),
            chargeId: c?.id ?? c?.ID,
            ...vendorDetails,
        }));
        data.charges = char_;
        setPrefillData(data);
    };




    // This function will handle both creating a new bill and updating an existing one based on the presence of editingBill
    // const handleSubmit = async (e) => {
    //     try {
    //         dispatch(callLoader(1));
    //         const subtotal = formData.lines.reduce(
    //             (sum, line) => sum + (parseFloat(line.amount) || 0),
    //             0,
    //         );
    //         const taxAmount = formData.lines.reduce(
    //             (sum, line) => sum + (parseFloat(line.tax_amount) || 0),
    //             0,
    //         );


    //         const payload = {
    //             ...formData,

    //             bill_date: formData?.bill_date,              // YYYY-MM-DD
    //             due_date: formData?.due_date,                  // YYYY-MM-DD
    //             vendor_id: String(formData?.vendor_id || ""),
    //             vendor_name: formData?.vendor_name || "",
    //             vendor_invoice_no: formData?.vendor_invoice_no || "",

    //             booking_id: String(formData?.booking_id || ""),
    //             origin_port_id: formData?.origin_port_id || "",
    //             destination_port_id: formData?.destination_port_id || "",
    //             currency_id: "USD",
    //             currency_code: "USD",
    //             exchange_rate: 1,
    //             subtotal: subtotal ?? 0,
    //             tax_amount: taxAmount ?? 0,
    //             total_amount: (subtotal ?? 0) + (taxAmount ?? 0),

    //             terms: formData?.terms || "",
    //             notes: formData?.notes || "",
    //             internal_notes: formData?.internal_notes || "",
    //             metadata: metadata || {},
    //             lines: formData?.lines || []
    //         };

    //         // console.log("Payload for bill submission:", payload);
    //         const url = editingBill
    //             ? `${API_URL}/api/accounting/bills/${editingBill.id}`
    //             : `${API_URL}/api/accounting/bills`;

    //         const response = await fetch(url, {
    //             method: editingBill ? "PUT" : "POST",
    //             headers: { "Content-Type": "application/json" },
    //             body: JSON.stringify(payload),
    //         });

    //         try {
    //             if (!response.ok) {
    //                 const error = await response.json();
    //                 throw new Error(error.detail || "Failed to save invoice");
    //             }

    //             const resJson = await response.json();
    //             console.log("response from invoice:", resJson);

    //             const data = await dispatch(getBookingDetails(resJson?.booking_id));

    //             if (data?.arCharges?.length && resJson?.lines?.length) {

    //                 const lineMap = new Map(
    //                     resJson.lines.map(line => [String(line.source_charge_id), line])
    //                 );

    //                 const updatedApCharges = data.apCharges.map(charge => {
    //                     const match = lineMap.get(String(charge.id));

    //                     if (match) {
    //                         return {
    //                             ...charge,
    //                             billId: resJson.id,
    //                             billNo: resJson.bill_no,
    //                             billStatus: resJson.status
    //                         };
    //                     }

    //                     return charge;
    //                 });

    //                 console.log("updated charges:", updatedApCharges);

    //                 const payload = {
    //                     ...data,
    //                     apCharges: updatedApCharges
    //                 };

    //                 const bookingResponse = await updateBookingDetails(payload);
    //                 console.log("booking update response:", bookingResponse);
    //             }

    //         } catch (error) {
    //             console.error("Error processing invoice:", error);
    //         }

    //         dispatch(
    //             showToast(
    //                 editingBill ? "Bill updated successfully" : "Bill created successfully",
    //                 "success",
    //                 true
    //             )
    //         );

    //         setTimeout(() => {
    //             setShowModal(false);
    //             setEditingBill(null);
    //             resetForm();
    //             fetchBills();
    //         }, 2000)

    //     } catch (error) {
    //         toast.error(error.message);
    //     } finally {
    //         dispatch(callLoader(0));
    //     }
    // };
    const handleSubmit = async () => {
        try {
            dispatch(callLoader(1));

            if (!formData?.lines || formData.lines.length === 0) {
                throw new Error("Bill must have at least one line");
            }

            const payload = buildVendorBillPayload(formData, metadata, {
                companyId,
                accountingDivision,
                metadata,
                existingDoc: editingBill,
            });
            const { division: billDivision } = payload;

            const url = editingBill
                ? `${API_URL}/api/accounting/bills/${editingBill.id}${billQueryParams(billDivision)}`
                : `${API_URL}/api/accounting/bills`;

            const resJson = await accountingFetch(url, {
                method: editingBill ? "PUT" : "POST",
                body: JSON.stringify(payload),
            });

            // ✅ Open dialog
            if (autoSync) {
                await handleSyncDecision(true);
            } else {
                setPendingBillData(resJson);
                setShowSyncDialog(true);
            }

        } catch (error) {
            console.error(error);
            toast.error(error.message);
            dispatch(callLoader(0));
        }
        finally {
            dispatch(callLoader(0));
        }
    };

    const handleSyncDecision = async (isSync) => {
        try {
            // ✅ Always create payload with isSync
            const division = resolveSyncDivision(pendingBillData);
            const syncPayload = buildArApSyncPayload({
                document: pendingBillData,
                chargeKind: SYNC_CHARGE_KIND.AP,
                syncType: "create",
                isSync,
                createdBy: username,
                division,
            });

            let bookingResponse = null;

            if (isSync) {
                bookingResponse = await executeArApSync(syncPayload, division);

                if (isArApSyncSuccess(bookingResponse)) {
                    console.log("✅ Sync success");
                } else {
                    toast.error("Sync failed");
                }
            }

            dispatch(
                showToast(
                    isSync
                        ? "Bill saved & synced"
                        : "Bill saved (sync skipped)",
                    "success",
                    true
                )
            );

            setTimeout(() => {
                setShowModal(false);
                setEditingBill(null);
                resetForm();
                fetchBills();
                // fetchSummary();
            }, 1200);

        } catch (error) {
            console.error(error);
            toast.error("Something went wrong");
        } finally {
            setShowSyncDialog(false);
            setPendingBillData(null);
            dispatch(callLoader(0));
        }
    };


    return (
        <div className="p-6 space-y-6 bg-slate-50 dark:bg-slate-900 min-h-screen">
            {/* Header */}
            <div className="bg-gradient-to-r from-cyan-600 via-sky-600 to-blue-700 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                            <FileText className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">{t("vendorBills")}</h1>
                            <p className="text-gray-200">{t("manageAP")}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-slate-500">
                    <p className="text-sm text-slate-500">{t("allBills")}</p>
                    <p className="text-2xl font-bold">{bills.length}</p>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-amber-500">
                    <p className="text-sm text-slate-500">{t("pending")}</p>
                    <p className="text-2xl font-bold">
                        {bills.filter((b) => b.status === "pending").length}
                    </p>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-green-500">
                    <p className="text-sm text-slate-500">{t("paid")}</p>
                    <p className="text-2xl font-bold">
                        {bills.filter((b) => b.status === "paid").length}
                    </p>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border-l-4 border-l-red-500">
                    <p className="text-sm text-slate-500">{t("outstanding")}</p>
                    <p className="text-xl font-bold via-sky-600">
                        {formatCurrency(totalOutstanding)}
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="rounded-lg border text-card-foreground shadow-sm overflow-hidden bg-card/50 backdrop-blur-sm">
                <div className="flex gap-4 items-center rounded-xl p-4 shadow-sm">
                    <div className="flex-1 max-w-md">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                placeholder={t("searchBills")}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                data-testid="search-bills"
                            />
                        </div>
                    </div>
                    <select
                        value={accountingDivision}
                        onChange={(e) => setAccountingDivision(e.target.value)}
                        className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                        data-testid="filter-bill-division"
                    >
                        <option value={ACCOUNTING_DIVISION.EXPORT}>
                            Export Booking
                        </option>
                        <option value={ACCOUNTING_DIVISION.IMPORT}>
                            Import Booking
                        </option>
                    </select>
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                        data-testid="filter-bill-status"
                    >
                        <option value="">{t("allStatuses")}</option>
                        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                            <option key={key} value={key}>
                                {config.label}
                            </option>
                        ))}
                    </select>
                    <Button
                        onClick={() => {
                            resetForm();
                            setEditingBill(null);
                            setShowModal(true);

                        }}
                        className="via-sky-600 hover:via-sky-700 text-white"
                        data-testid="new-bill-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" /> {t("newBill")}
                    </Button>
                </div>

                {/* Table */}
                <DataTable
                    loading={loading}
                    data={bills || []}
                    columns={columns}
                    boxClass="bg-transaprent"
                    trClass={
                        "hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    }
                    thClass={
                        "px-3 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 white whitespace-nowrap uppercase tracking-wider"
                    }
                    tdClass=" text-sm  px-2 py-2 border-b border-slate-200 dark:border-slate-700 whitespace-nowrap"
                    htrClass="border-b border-slate-200 dark:border-slate-800 bg-transparen"
                    emptyText="No bills found"
                />
            </div>

            {/* Modal */}
            {showModal && (
                <EditInvoiceModel
                    type="BILL"
                    onClose={() => {
                        setShowModal(false);
                        setEditingBill(null);
                    }}
                    editingInvoice={editingBill}
                    formData={formData}
                    setFormData={setFormData}
                    handleSubmit={handleSubmit}
                    changeHandler={changeHandler}
                    onDelete={(data) => {
                        handleDelete(editingBill.id);
                    }}
                    metadata={metadata}
                    setMetadata={setMetadata}
                    setShowLinkChargesDialog={() => { }}
                    handlePrint={() => onPrint(editingBill)}
                    handleEmail={() => onPrint(editingBill)}
                    handleTechGuide={() => setTechGuideOpen(true)}
                    handleInvoiceLog={() => { }}
                    handleNewInvoice={() => resetForm()}
                    loading={false}
                    fetchBook={loadBookingData}
                    accountingDivision={accountingDivision}
                    syncChargeKind={SYNC_CHARGE_KIND.AP}
                />
            )}

            <ExportDialog
                open={!!showExport}
                onOpenChange={() => {
                    setShowExport("");
                    setPrintData(null);
                }}
                mode={showExport}
                email={{
                    subject: `Bill ${printData?.bill_no} - ${printData?.vendor_name}`,
                    body: `Dear ${printData?.vendor_name || "Vendor"},

                    Please find attached Bill ${printData?.billNo} for the amount of $${Number(
                        printData?.total_amount || 0
                    ).toLocaleString("en-US", { minimumFractionDigits: 2 })}.

                    Payment is due by ${printData?.due_date}.

                    Best regards,
                    Shipez.ai Team`,
                }}
                data={printData}
                pageTitle={`${printData?.invoiceType || "Bill"} - ${printData?.bill_no}`}
                heading="Bill"
                ExportFileContent={generateExportContent}
            >
                <BillPrintView />
            </ExportDialog>

            <ConfirmSyncDialog
                open={showSyncDialog}
                onClose={setShowSyncDialog}
                onConfirm={handleSyncDecision}
                title="Bill"
                description="Do you want to sync this bill with shipment data?"
            />

        </div>
    );
};

export default VendorBills;
